import React, { useState, useEffect } from 'react';
import Card from './Card';
import DropdownButton from './DropdownButton';
import InputField from './InputField';
import Button from './Button';
import { fetchNeisTimetable, getAcademicYearAndSemester } from '../../utils/neis';
import { fetchComciganTimetable } from '../../utils/comcigan';

// 시간표 데이터 인터페이스
interface TimetableEntry {
  period: number;        // 교시 (1-7)
  time: string;          // 시간 (예: "09:00")
  monday?: string;       // 월요일 과목
  tuesday?: string;      // 화요일 과목
  wednesday?: string;    // 수요일 과목
  thursday?: string;     // 목요일 과목
  friday?: string;       // 금요일 과목
}

interface Timetable {
  semester: '1학기' | '2학기';
  year: number;
  entries: TimetableEntry[];
}

/** 나이스 연동용 학교/학급 정보 */
interface NeisContext {
  neisOfficeCode: string;
  neisSchoolCode: string;
  schoolType: 'elementary' | 'middle' | 'high';
  grade: number;
  classNum: number;
}

/** 컴시간알리미 연동용 학교/학급 정보 */
interface ComciganContext {
  schoolCode: number;
  grade: number;
  classNum: number;
}

// TimetableEditDialog props 인터페이스
interface TimetableEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onTimetableSaved: (timetable: Timetable) => void;
  scale?: number;
  initialData?: Timetable;
  /** 나이스에서 불러오기 가능 시 전달 (학교 NEIS 코드 + 학급 정보) */
  neisContext?: NeisContext | null;
  /** 컴시간알리미에서 불러오기 가능 시 전달 */
  comciganContext?: ComciganContext | null;
}

const TimetableEditDialog: React.FC<TimetableEditDialogProps> = ({
  isOpen,
  onClose,
  onTimetableSaved,
  scale = 1,
  initialData,
  neisContext,
  comciganContext
}) => {
  const [semester, setSemester] = useState<'1학기' | '2학기'>(
    () => (getAcademicYearAndSemester().semester === 1 ? '1학기' : '2학기')
  );
  const [loadingNeis, setLoadingNeis] = useState(false);
  const [loadingComcigan, setLoadingComcigan] = useState(false);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [year, setYear] = useState(() => getAcademicYearAndSemester().year);
  const [entries, setEntries] = useState<TimetableEntry[]>([
    { period: 1, time: '09:00', monday: '', tuesday: '', wednesday: '', thursday: '', friday: '' },
    { period: 2, time: '10:00', monday: '', tuesday: '', wednesday: '', thursday: '', friday: '' },
    { period: 3, time: '11:00', monday: '', tuesday: '', wednesday: '', thursday: '', friday: '' },
    { period: 4, time: '11:30', monday: '', tuesday: '', wednesday: '', thursday: '', friday: '' },
    { period: 5, time: '13:20', monday: '', tuesday: '', wednesday: '', thursday: '', friday: '' },
    { period: 6, time: '14:10', monday: '', tuesday: '', wednesday: '', thursday: '', friday: '' },
    { period: 7, time: '15:00', monday: '', tuesday: '', wednesday: '', thursday: '', friday: '' },
  ]);

  const semesterOptions = [
    { value: '1학기', label: '1학기' },
    { value: '2학기', label: '2학기' }
  ];

  // 다이얼로그가 열릴 때 데이터 초기화
  useEffect(() => {
    if (isOpen) {
      const academic = getAcademicYearAndSemester();
      setYear(academic.year);
      if (initialData) {
        setSemester(initialData.semester);
        setEntries(initialData.entries);
      } else {
        setSemester(academic.semester === 1 ? '1학기' : '2학기');
      }
      setSourceError(null);
    }
  }, [isOpen, initialData]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();

    const timetableData: Timetable = {
      semester,
      year,
      entries
    };

    onTimetableSaved(timetableData);
  };

  const handleClose = () => {
    const academic = getAcademicYearAndSemester();
    setSemester(academic.semester === 1 ? '1학기' : '2학기');
    setYear(academic.year);
    setEntries([
      { period: 1, time: '09:00', monday: '', tuesday: '', wednesday: '', thursday: '', friday: '' },
      { period: 2, time: '10:00', monday: '', tuesday: '', wednesday: '', thursday: '', friday: '' },
      { period: 3, time: '11:00', monday: '', tuesday: '', wednesday: '', thursday: '', friday: '' },
      { period: 4, time: '11:30', monday: '', tuesday: '', wednesday: '', thursday: '', friday: '' },
      { period: 5, time: '13:20', monday: '', tuesday: '', wednesday: '', thursday: '', friday: '' },
      { period: 6, time: '14:10', monday: '', tuesday: '', wednesday: '', thursday: '', friday: '' },
      { period: 7, time: '15:00', monday: '', tuesday: '', wednesday: '', thursday: '', friday: '' },
    ]);
    onClose();
  };

  const handleSubjectChange = (periodIndex: number, day: keyof TimetableEntry, value: string) => {
    setEntries(prev => prev.map((entry, idx) => 
      idx === periodIndex 
        ? { ...entry, [day]: value }
        : entry
    ));
  };

  const handleLoadFromNeis = async () => {
    if (!neisContext) return;
    setLoadingNeis(true);
    setSourceError(null);
    try {
      const timetable = await fetchNeisTimetable({
        officeCode: neisContext.neisOfficeCode,
        schoolCode: neisContext.neisSchoolCode,
        year,
        semester: semester === '1학기' ? 1 : 2,
        grade: neisContext.grade,
        classNum: neisContext.classNum,
        schoolType: neisContext.schoolType,
      });
      setEntries(timetable.entries);
      setSemester(timetable.semester);
    } catch (err) {
      setSourceError(err instanceof Error ? err.message : '나이스에서 시간표를 불러오는데 실패했습니다.');
    } finally {
      setLoadingNeis(false);
    }
  };

  const handleLoadFromComcigan = async () => {
    if (!comciganContext) return;
    setLoadingComcigan(true);
    setSourceError(null);
    try {
      const timetable = await fetchComciganTimetable({
        schoolCode: comciganContext.schoolCode,
        grade: comciganContext.grade,
        classNum: comciganContext.classNum,
      });
      setEntries(timetable.entries);
      setSemester(timetable.semester);
      setYear(timetable.year);
    } catch (err) {
      setSourceError(err instanceof Error ? err.message : '컴시간에서 시간표를 불러오는데 실패했습니다.');
    } finally {
      setLoadingComcigan(false);
    }
  };

  const canLoadFromNeis = Boolean(neisContext?.neisOfficeCode && neisContext?.neisSchoolCode);
  const canLoadFromComcigan = Boolean(comciganContext?.schoolCode && comciganContext.schoolCode > 0);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <Card 
        width="1000px" 
        height="733px" 
        padding="24px 28px 20px 28px" 
        gap="24px" 
        style={{
          transformOrigin: 'center center',
          transform: `scale(${scale})`,
          overflow: 'hidden'
        }}>
        {/* 헤더 */}
        <div style={{
          width: '100%',
          height: '32px',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px'
        }}>
          <h2 style={{
            fontSize: '20px',
            lineHeight: '30px',
            fontWeight: '600',
            color: '#161616',
            margin: 0
          }}>
            시간표 수정하기
          </h2>
          <img 
            src="/images/icons/closeIcon.svg"
            alt="닫기"
            onClick={handleClose}
            style={{
              width: '32px',
              height: '32px',
              padding: '9.4px',
              cursor: 'pointer'
            }}
          />
        </div>

        {/* 학기 선택 + 나이스 불러오기 */}
        <div style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          gap: '12px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <div style={{
            fontSize: '16px',
            fontWeight: '600',
            color: 'var(--Color-text-normal, #161616)'
          }}>
            {year}년
          </div>
          <DropdownButton
            options={semesterOptions}
            value={semester}
            onChange={(value) => setSemester(value as '1학기' | '2학기')}
            placeholder="학기"
            width="128px"
            height="48px"
          />
          {(canLoadFromNeis || canLoadFromComcigan) && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {canLoadFromNeis && (
                <Button
                  variant="outline"
                  size="medium"
                  onClick={handleLoadFromNeis}
                  disabled={loadingNeis || loadingComcigan}
                >
                  {loadingNeis ? '불러오는 중...' : '나이스에서 불러오기'}
                </Button>
              )}
              {canLoadFromComcigan && (
                <Button
                  variant="outline"
                  size="medium"
                  onClick={handleLoadFromComcigan}
                  disabled={loadingNeis || loadingComcigan}
                >
                  {loadingComcigan ? '불러오는 중...' : '컴시간에서 불러오기'}
                </Button>
              )}
            </div>
          )}
        </div>
        {sourceError && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#FFF5F5',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#C53030'
          }}>
            {sourceError}
          </div>
        )}

        {/* 시간표 그리드 */}
        <div style={{
          width: '100%',
          height: '520px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          overflowY: 'auto'
        }}>
          {/* 헤더 */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '60px 100px repeat(5, 1fr)',
            gap: '12px',
            padding: '12px',
            background: 'var(--Color-background-sub, #F8F8F8)',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            color: 'var(--Color-text-normal, #161616)',
            textAlign: 'center'
          }}>
            <div>교시</div>
            <div>시간</div>
            <div>월</div>
            <div>화</div>
            <div>수</div>
            <div>목</div>
            <div>금</div>
          </div>

          {/* 시간표 입력 행들 */}
          {entries.map((entry, index) => (
            <div key={entry.period} style={{
              display: 'grid',
              gridTemplateColumns: '60px 100px repeat(5, 1fr)',
              gap: '12px',
              alignItems: 'center',
              paddingBottom: '8px',
              borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)'
            }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--Color-text-normal, #161616)',
                textAlign: 'center'
              }}>
                {entry.period}교시
              </div>
              <InputField
                type="time"
                value={entry.time}
                onChange={(value) => {
                  setEntries(prev => prev.map((e, idx) => 
                    idx === index ? { ...e, time: value } : e
                  ));
                }}
                width="100%"
                height="40px"
              />
              <InputField
                type="text"
                value={entry.monday || ''}
                onChange={(value) => handleSubjectChange(index, 'monday', value)}
                placeholder="과목"
                width="100%"
                height="40px"
              />
              <InputField
                type="text"
                value={entry.tuesday || ''}
                onChange={(value) => handleSubjectChange(index, 'tuesday', value)}
                placeholder="과목"
                width="100%"
                height="40px"
              />
              <InputField
                type="text"
                value={entry.wednesday || ''}
                onChange={(value) => handleSubjectChange(index, 'wednesday', value)}
                placeholder="과목"
                width="100%"
                height="40px"
              />
              <InputField
                type="text"
                value={entry.thursday || ''}
                onChange={(value) => handleSubjectChange(index, 'thursday', value)}
                placeholder="과목"
                width="100%"
                height="40px"
              />
              <InputField
                type="text"
                value={entry.friday || ''}
                onChange={(value) => handleSubjectChange(index, 'friday', value)}
                placeholder="과목"
                width="100%"
                height="40px"
              />
            </div>
          ))}
        </div>

        {/* 버튼 */}
        <div style={{
          width: '100%',
          height: '45px',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px'
        }}>
          <Button variant="cancel" size="medium"
            children="취소"
            onClick={handleClose}
            width="124px"
          />
          <Button size="medium" variant="submit"
            children="저장"
            onClick={() => handleSubmit()}
            width="822px"
          />
        </div>
      </Card>
    </div>
  );
};

export default TimetableEditDialog;

